import React, { useState, useEffect } from 'react';
import { MessageCircle, Clock, DollarSign } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../lib/api';
import { chatApi } from '../../lib/chat';
import { consultantsApi, type ApiConsultantProfile } from '../../lib/consultants';
import { toast } from 'sonner';
import { Button } from '../common/Button';
import { ConsultantChat } from './ConsultantChat';

interface ChatSessionView {
  id: string;
  userName: string;
  unread: number;
  createdAt: string;
}

export const ConsultantDashboard: React.FC = () => {
  const { user } = useAuth();
  const [consultant, setConsultant] = useState<ApiConsultantProfile | null>(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [activeChats, setActiveChats] = useState<ChatSessionView[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatSessionView | null>(null);

  useEffect(() => {
    if (user?.role !== 'consultant') return;

    // Perfil do próprio consultor (404 = registro consultants ausente)
    consultantsApi.me()
      .then(({ consultant: c }) => {
        setConsultant(c);
        setIsAvailable(c.status === 'active');
      })
      .catch(err => {
        console.error('Error loading consultant profile:', err);
        if (err instanceof ApiError && err.status === 404) {
          toast.error('Perfil de consultor não encontrado');
        }
      });

    loadActiveChats();
    const interval = setInterval(loadActiveChats, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const loadActiveChats = async () => {
    try {
      const { sessions } = await chatApi.listSessions();
      const myChatSessions = sessions.filter(s => s.sessionType === 'consultant');
      setActiveChats(myChatSessions.map(s => ({
        id: s.id,
        userName: s.userName ?? `sessão ${s.id.slice(0, 8)}`,
        unread: s.unread ?? 0,
        createdAt: s.createdAt,
      })));
    } catch (err) {
      console.error('Error loading chats:', err);
      if (!(err instanceof ApiError) || err.status !== 404) {
        toast.error('Erro ao carregar chats ativos');
      }
    }
  };

  const toggleAvailability = async () => {
    try {
      const { consultant: updated } = await consultantsApi.patchMe({ available: !isAvailable });
      setConsultant(updated);
      setIsAvailable(updated.status === 'active');
      toast.success(updated.status === 'active' ? 'Status: Disponível' : 'Status: Indisponível');
    } catch (err) {
      toast.error('Erro ao atualizar disponibilidade');
      console.error('ConsultantDashboard: erro de disponibilidade', err);
    }
  };

  const handleChatClick = (chat: ChatSessionView) => {
    setSelectedChat(chat);
  };

  const handleCloseChat = () => {
    setSelectedChat(null);
    loadActiveChats();
  };

  if (!user || user.role !== 'consultant') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-purple-200 mb-2">Acesso Restrito</h2>
          <p className="text-purple-200/70">Esta área é apenas para consultores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-purple-200">Dashboard do Consultor</h2>
        <Button
          onClick={toggleAvailability}
          variant={isAvailable ? 'primary' : 'secondary'}
        >
          {isAvailable ? 'Disponível' : 'Indisponível'}
        </Button>
      </div>
      {consultant && (
        <p className="text-purple-200/70">
          Perfil: {consultant.displayName}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <MessageCircle className="w-6 h-6 text-purple-300" />
            <h3 className="text-lg font-medium text-purple-200">Consultas Hoje</h3>
          </div>
          <p className="text-3xl font-bold text-purple-200">{activeChats.length}</p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="w-6 h-6 text-purple-300" />
            <h3 className="text-lg font-medium text-purple-200">Minutos Online</h3>
          </div>
          <p className="text-3xl font-bold text-purple-200">
            0
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <DollarSign className="w-6 h-6 text-purple-300" />
            <h3 className="text-lg font-medium text-purple-200">Ganhos Hoje</h3>
          </div>
          <p className="text-3xl font-bold text-purple-200">
            R$ 0,00
          </p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
        <h3 className="text-lg font-medium text-purple-200 mb-4">Chats Ativos</h3>
        {activeChats.length === 0 ? (
          <p className="text-purple-200/70">Nenhum chat ativo no momento</p>
        ) : (
          <div className="space-y-4">
            {activeChats.map((chat) => (
              <div key={chat.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="text-purple-200">
                    {chat.userName}
                    {chat.unread > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold rounded-full bg-purple-500 text-white">
                        {chat.unread}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-purple-200/70">
                    Iniciada às {new Date(chat.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleChatClick(chat)}
                >
                  Acessar Chat
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedChat && (
        <ConsultantChat
          sessionId={selectedChat.id}
          userName="Usuário"
          onClose={handleCloseChat}
        />
      )}
    </div>
  );
};